import type { BookingItem, BookingListItem } from 'types/booking';
import type {
  BookingFilters,
  FilterAggregations,
  FilterableState,
  Pagination,
} from 'types/filters';
import type { FiltersActionPayload } from '../../reducers/filters';
import { formatDate } from '../../reducers/filters';
import type { Report, ReportableState } from '../../reducers/reports';
import { dynamicFilters } from './dynamicFilters';

export const actions = {
  FETCH_BOOKINGS_SUCCESS: 'FETCH_BOOKINGS_SUCCESS',
  FETCH_BOOKING_ITEMS_SUCCESS: 'FETCH_BOOKING_ITEMS_SUCCESS',
  FETCH_ALL_BOOKINGS_METADATA: 'FETCH_ALL_BOOKINGS_METADATA',
  FETCH_ALL_BOOKING_ITEMS_METADATA: 'FETCH_ALL_BOOKING_ITEMS_METADATA',
  SET_SORT_BY: 'SET_SORT_BY',
  CHANGE_PAGINATION: 'CHANGE_PAGINATION',
  RESET: 'RESET',
  RESET_EXCEPT_FILTERS: 'RESET_EXCEPT_FILTERS',
} as const;

// Composed with filtersReducer and reportsReducer via `combineReducers`, so the
// state has to satisfy what both of those read.
export interface BookingsState extends FilterableState, ReportableState {
  bookings: BookingListItem[];
  bookingItems: BookingItem[];
  // Counts are '' until the first response has been received.
  allBookingsCount: number | string;
  totalCount: number | string;
  count: number | string;
  error: boolean;
  filtersEnabled: boolean;
  applyFilters: boolean;
  sortBy: string;
  initialized: boolean;
  filters: BookingFilters;
  pagination: Pagination;
  previousRequestFilters: Partial<BookingFilters>;
  apiCheckedDefaultFilters: BookingFilters;
  report: Report;
  loading?: boolean;
}

// One payload type covering this reducer's actions plus the filter and report
// actions, because all three reducers see every dispatch.
export interface BookingsActionPayload extends FiltersActionPayload {
  bookings?: BookingListItem[];
  bookingItems?: BookingItem[];
  meta?: {
    pagination: Pagination;
    aggs: FilterAggregations;
  };
  allBookingsCount?: number | string;
  allBookingItemsCount?: number | string;
  filters?: Partial<BookingFilters>;
  sortBy?: string;
  page?: number;
  perPage?: number;
  report?: Report;
}

export interface BookingsAction {
  type: string;
  payload?: BookingsActionPayload;
}

export const defaultFilters: BookingFilters = {
  keywordSearch: '',
  startDate: '',
  endDate: '',
  statuses: [],
  createdByUsers: [],
  venues: [],
  spaces: [],
  services: [],
  paymentStatuses: [],
};

export const initialState: BookingsState = {
  bookings: [],
  bookingItems: [],
  allBookingsCount: '',
  error: false,
  filtersEnabled: true,
  sortBy: 'ref',
  totalCount: '',
  applyFilters: true,
  count: '',
  initialized: false,
  filters: { ...defaultFilters },
  pagination: {
    currentPage: 1,
    perPage: 10,
    totalCount: '',
    count: '',
  },
  previousRequestFilters: {},
  apiCheckedDefaultFilters: { ...defaultFilters },
  report: {},
};

// Stamps today's date onto the default start date, so the list opens on
// "from today" rather than an empty range.
export const initialiseState = ({
  filters = defaultFilters,
  apiCheckedDefaultFilters = defaultFilters,
  ...state
}: BookingsState = initialState): BookingsState => {
  const startDate = formatDate();

  return {
    ...state,
    filters: { ...filters, startDate },
    apiCheckedDefaultFilters: { ...apiCheckedDefaultFilters, startDate },
  };
};

const reducer = (
  state: BookingsState,
  action: BookingsAction
): BookingsState => {
  // Each case below knows which fields its own dispatch supplies; the payload is
  // optional only because the filter and report actions share this type.
  const payload = action.payload ?? {};

  switch (action.type) {
    case actions.RESET:
      return initialiseState(initialState);
    case actions.RESET_EXCEPT_FILTERS:
      return {
        ...initialiseState(initialState),
        filters: state.filters,
        apiCheckedDefaultFilters: state.apiCheckedDefaultFilters,
      };
    case actions.SET_SORT_BY:
      return {
        ...state,
        sortBy: payload.sortBy!,
        pagination: { ...state.pagination, currentPage: 1 },
      };
    case actions.FETCH_ALL_BOOKINGS_METADATA:
      return {
        ...state,
        allBookingsCount: payload.allBookingsCount!,
        filters: { ...state.filters, ...payload.filters },
        apiCheckedDefaultFilters: {
          ...state.filters,
          ...payload.filters,
        },
      };
    case actions.FETCH_ALL_BOOKING_ITEMS_METADATA:
      return {
        ...state,
        allBookingsCount: payload.allBookingItemsCount!,
        filters: { ...state.filters, ...payload.filters },
        apiCheckedDefaultFilters: {
          ...state.filters,
          ...payload.filters,
        },
      };
    case actions.CHANGE_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          currentPage: payload.page!,
          perPage: payload.perPage!,
        },
      };
    case actions.FETCH_BOOKINGS_SUCCESS: {
      const updatedFilters = state.initialized
        ? dynamicFilters(state.filters, payload.meta!.aggs)
        : state.filters;
      return {
        ...state,
        initialized: true,
        bookings: payload.bookings!,
        pagination: {
          ...payload.meta!.pagination,
          currentPage: state.pagination.currentPage,
        },
        filters: updatedFilters,
        previousRequestFilters: updatedFilters,
      };
    }
    case actions.FETCH_BOOKING_ITEMS_SUCCESS: {
      const updatedFilters = state.initialized
        ? dynamicFilters(state.filters, payload.meta!.aggs)
        : state.filters;
      return {
        ...state,
        initialized: true,
        bookingItems: payload.bookingItems!,
        pagination: payload.meta!.pagination,
        filters: updatedFilters,
        previousRequestFilters: updatedFilters,
      };
    }
    default:
      return state;
  }
};

export default reducer;
